// Katalog data point Custom Chart (cuma sensor). Murni, nol import.
// agg = [fungsi, param, prefix key di `additional`], terverifikasi ke API 11 Sep 2026.
// Param yang gak dikenal bikin backend error SQL buat seluruh request.

export const CATALOG = {
  rpm: [
    {
      point: 'rpm', label: 'RPM', unit: 'rpm', channel: 'position', agg: ['AVGGT', '@Rpm', 'AVGGT_dlrpRpm'],
    },
  ],
  ae: [
    {
      point: 'rpm', label: 'AE RPM', unit: 'rpm', channel: 'ae', agg: ['AVG', '@Rpm', 'AVG_dlaeRpm'],
    },
  ],
  temperature: [
    {
      point: 'temperature', label: 'Temperature', unit: '°C', channel: 'position', agg: ['AVG', '@Temp', 'AVG_dltmTemp'],
    },
  ],
  flowmeter: [
    {
      point: 'volumeFlowrate', label: 'Volume Flowrate', unit: 'L/h', agg: ['AVG', '@VolFlowrate', 'AVG_dlfmVolFlowrate'],
    },
    {
      point: 'massFlowrate', label: 'Mass Flowrate', unit: 'kg/h', agg: ['AVG', '@MassFlowrate', 'AVG_dlfmMassFlowrate'],
    },
    {
      point: 'density', label: 'Density', unit: 'g/cm³', agg: ['AVG', '@Density', 'AVG_dlfmDensity'],
    },
    {
      point: 'temperature', label: 'Fuel Temperature', unit: '°C', agg: ['AVG', '@Temp', 'AVG_dlfmTemp'],
    },
    {
      point: 'volumeTotal', label: 'Volume Total', unit: 'L', agg: ['LAST', '@VolTotal', 'LAST_dlfmVolTotal'],
    },
    {
      point: 'massTotal', label: 'Mass Total', unit: 'kg', agg: ['LAST', '@MassTotal', 'LAST_dlfmMassTotal'],
    },
    {
      point: 'volumeInventory', label: 'Volume Inventory', unit: 'L', agg: ['LAST', '@VolInventory', 'LAST_dlfmVolInventory'],
    },
    {
      point: 'massInventory', label: 'Mass Inventory', unit: 'kg', agg: ['LAST', '@MassInventory', 'LAST_dlfmMassInventory'],
    },
  ],
  gps: [
    {
      point: 'speed', label: 'Speed', unit: 'knot', scale: 0.539957, agg: ['AVG', '@Speed', 'AVG_dlgpSpeed'],
    },
    // Rata-rata sudut gak bermakna, jadi pakai nilai terakhir.
    {
      point: 'track', label: 'Heading', unit: '°', agg: ['LAST', '@Track', 'LAST_dlgpTrack'],
    },
    {
      point: 'satellites', label: 'Satellites', unit: 'count', agg: ['AVG', '@Satellites', 'AVG_dlgpSatellites'],
    },
    {
      point: 'altitude', label: 'Altitude', unit: 'm', agg: ['AVG', '@Altitude', 'AVG_dlgpAltitude'],
    },
    {
      point: 'waveHeight', label: 'Wave Height', unit: 'm', agg: ['AVG', '@WaveHeight', 'AVG_dlgpWaveHeight'],
    },
    {
      point: 'wavePeriod', label: 'Wave Period', unit: 's', agg: ['AVG', '@WavePeriod', 'AVG_dlgpWavePeriod'],
    },
  ],
  limitswitch: [
    {
      point: 'duration', label: 'Switch On Duration', unit: 's', agg: ['SUM', '@Duration', 'SUM_dlswDuration'],
    },
  ],
  // Belum ada di company uji; nama field dari lastPowermeterData.
  powermeter: [
    { point: 'activePower', label: 'Active Power', unit: 'kW' },
    { point: 'apparentPower', label: 'Apparent Power', unit: 'kVA' },
    { point: 'voltageLtoL', label: 'Voltage L-L', unit: 'V' },
    { point: 'current', label: 'Current', unit: 'A' },
    { point: 'frequency', label: 'Frequency', unit: 'Hz' },
    { point: 'powerFactor', label: 'Power Factor', unit: 'pf' },
  ],
};

// Baris per bucket, buat hitung potongan terhadap batas 6000 baris.
export const ROWS_PER_BUCKET = { rpm: 3, temperature: 3, ae: 4 };

export const DEFAULT_CHANNELS = {
  position: ['PORT', 'STARBOARD', 'CENTER'],
  ae: ['AE1', 'AE2', 'AE3'],
};

const POSITION_ORDER = ['PORT', 'STARBOARD', 'CENTER'];

export function pointsFor(deviceType) {
  return CATALOG[deviceType] || [];
}

export function findPoint(deviceType, point) {
  return pointsFor(deviceType).find((entry) => entry.point === point) || null;
}

export function channelModeOf(deviceType) {
  const first = pointsFor(deviceType)[0];
  return (first && first.channel) || null;
}

export function requestParamsFor(deviceType) {
  const withAgg = pointsFor(deviceType).filter((entry) => entry.agg);
  if (!withAgg.length) return {};
  return {
    functionNames: withAgg.map((entry) => entry.agg[0]),
    functionParams: withAgg.map((entry) => entry.agg[1]),
  };
}

export function channelOfRow(deviceType, row) {
  const mode = channelModeOf(deviceType);
  if (!mode || !row) return null;
  if (mode === 'ae') {
    const no = Number(row.no);
    return Number.isInteger(no) && no >= 0 ? `AE${no + 1}` : null;
  }
  // Sensor suhu bisa punya lebih dari satu probe; yang dipakai cuma probe pertama.
  if (deviceType === 'temperature' && Number(row.no) > 0) return null;
  const type = String(row.type || '').toUpperCase();
  if (!type) return null;
  return type === 'MAINENGINE' || type === 'MAIN' ? 'CENTER' : type;
}

function pickAggregated(row, prefix) {
  const additional = row && row.additional;
  if (!additional || Array.isArray(additional)) return undefined;
  const key = Object.keys(additional).find((k) => k.startsWith(prefix));
  return key === undefined ? undefined : additional[key];
}

export function valueOfRow(deviceType, point, row) {
  const entry = findPoint(deviceType, point);
  if (!entry || !row) return null;
  const aggregated = entry.agg ? pickAggregated(row, entry.agg[2]) : undefined;
  const raw = aggregated !== undefined ? aggregated : row[point];
  if (raw === null || raw === undefined || raw === '') return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return entry.scale ? num * entry.scale : num;
}

function channelRank(channel) {
  const i = POSITION_ORDER.indexOf(channel);
  return i === -1 ? 99 : i;
}

export function channelsFromRows(deviceType, rows) {
  const found = new Set();
  (rows || []).forEach((row) => {
    const channel = channelOfRow(deviceType, row);
    if (channel) found.add(channel);
  });
  return Array.from(found).sort((a, b) => (
    channelRank(a) - channelRank(b) || a.localeCompare(b, 'en', { numeric: true })
  ));
}
